'use client';
import React from 'react';
import { useForm } from 'react-hook-form';
import { X, Save } from 'lucide-react';
import type { OwnerListing } from './mockOwnerData';
import { checkBannedWords } from '@/lib/contentModeration';

interface Props {
  listing: OwnerListing;
  onSave: (updated: OwnerListing) => void;
  onClose: () => void;
}

interface EditFormData {
  title: string;
  price: number;
  area: number;
  city: string;
  district: string;
  mode: 'sale' | 'rent';
  furnished: boolean;
}

export default function EditListingModal({ listing, onSave, onClose }: Props) {
  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<EditFormData>({
    defaultValues: {
      title: listing.title,
      price: listing.price,
      area: listing.area,
      city: listing.city,
      district: listing.district,
      mode: listing.mode,
      furnished: listing.furnished ?? false,
    },
  });

  const onSubmit = (data: EditFormData) => {
    // Check banned words in title
    const bannedInTitle = checkBannedWords(data.title);
    if (bannedInTitle) {
      alert('العنوان يحتوي على محتوى غير مسموح به. يرجى مراجعة النص.');
      return;
    }

    onSave({
      ...listing,
      ...data,
      priceUnit: data.mode === 'sale' ? 'AED' : 'AED/شهر',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-gray-100 z-10">
          <h3 className="font-600 text-gray-900">تعديل الإعلان</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-600 text-gray-700 mb-1">عنوان الإعلان</label>
            <input
              type="text"
              {...register('title', { required: 'العنوان مطلوب', minLength: { value: 10, message: 'العنوان قصير جداً' } })}
              className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right ${errors.title ? 'border-red-300' : 'border-gray-200'}`}
            />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title.message}</p>}
          </div>

          {/* Mode */}
          <div>
            <label className="block text-sm font-600 text-gray-700 mb-1">نوع الإعلان</label>
            <div className="flex gap-3">
              {(['sale', 'rent'] as const).map(m => (
                <label key={`edit-mode-${m}`} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" value={m} {...register('mode')} className="text-primary-500" />
                  <span className="text-sm text-gray-700">{m === 'sale' ? 'للبيع' : 'للإيجار'}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Price + Area */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-600 text-gray-700 mb-1">السعر (AED)</label>
              <input
                type="number"
                {...register('price', { required: 'السعر مطلوب', min: { value: 1, message: 'السعر يجب أن يكون أكبر من 0' } })}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right tabular-nums ${errors.price ? 'border-red-300' : 'border-gray-200'}`}
              />
              {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-600 text-gray-700 mb-1">المساحة (م²)</label>
              <input
                type="number"
                {...register('area', { required: 'المساحة مطلوبة', min: { value: 1, message: 'المساحة يجب أن تكون أكبر من 0' } })}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right tabular-nums ${errors.area ? 'border-red-300' : 'border-gray-200'}`}
              />
              {errors.area && <p className="text-xs text-red-500 mt-1">{errors.area.message}</p>}
            </div>
          </div>

          {/* City + District */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-600 text-gray-700 mb-1">المدينة</label>
              <input
                type="text"
                {...register('city', { required: 'المدينة مطلوبة' })}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right ${errors.city ? 'border-red-300' : 'border-gray-200'}`}
              />
              {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-600 text-gray-700 mb-1">الحي</label>
              <input
                type="text"
                {...register('district', { required: 'الحي مطلوب' })}
                className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 text-right ${errors.district ? 'border-red-300' : 'border-gray-200'}`}
              />
              {errors.district && <p className="text-xs text-red-500 mt-1">{errors.district.message}</p>}
            </div>
          </div>

          {/* Furnished */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" {...register('furnished')} className="w-4 h-4 rounded border-gray-300 text-primary-500" />
            <span className="text-sm text-gray-700">مفروشة</span>
          </label>

          {/* Unsaved changes indicator */}
          {isDirty && (
            <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
              <span>⚠</span>
              لديك تغييرات غير محفوظة
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 text-sm font-600 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="flex-1 py-2.5 text-sm font-600 text-white bg-primary-500 hover:bg-primary-600 rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <Save size={15} />
              حفظ التعديلات
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}